/**
 * linkedinPresenceContent.ts
 *
 * Content for LinkedIn Presence Builder (Product #6).
 *
 * Prompt kit + post archetypes + outreach templates specifically sharpened
 * for agentic AI job seekers in the 2026 market. Every prompt references
 * the 7-domain framework and the agentic AI vocabulary so the product
 * integrates with Resume Mastery and the Interview Playbook.
 *
 * EXEMPLAR POSTS are inspired by real brand-voice posts (anonymized to
 * remove PII) and demonstrate the structural patterns candidates should
 * emulate.
 *
 * TIER MAPPING
 * ────────────
 *   Headline rewriter              FREE
 *   News Commentary archetype      FREE
 *   All other prompts              All Access
 */

import { Localized, L } from "../utils/localized";

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

export interface LinkedInPrompt {
  slug: string;
  section: "profile" | "content" | "outreach" | "analytics";
  title: string;
  tagline: string;
  whatItDoes: string[];
  promptText: string;
  inputsRequired: string[];
  timeEstimate: string;
  tier: "free" | "all_access";
  agenticAIGuidance: string;
}

export interface PostArchetype {
  slug: string;
  name: string;
  purpose: string;
  idealFrequency: string;        // e.g. "3–4 per week"
  structure: string[];           // the reusable structural pattern
  hookFormulas: string[];        // 3–5 hook opening patterns
  cta: string;                   // typical closing CTA pattern
  exemplar: {
    headline: string;
    body: string;                // the full exemplar post
    whyItWorks: string[];        // 3–4 bullets on why it performs
  };
  tier: "free" | "all_access";
}

export interface OutreachTemplate {
  slug: string;
  persona: string;               // "Practitioner at target company", "Hiring manager", etc.
  goal: string;
  template: string;              // the copy-paste template
  principle: string;             // the core principle (e.g. "Lead with curiosity")
  doExample: string;
  dontExample: string;
  followUp?: string;             // optional follow-up template
}

// ---------------------------------------------------------------------------
// PROFILE OPTIMIZATION PROMPTS
// ---------------------------------------------------------------------------

export const PROFILE_PROMPTS: Localized<LinkedInPrompt[]> = {
  en: [

  {
    slug: "headline-rewriter",
    section: "profile",
    title: "Headline Rewriter",
    tagline: "Turn an invisible headline into a recruiter-search-friendly magnet.",
    whatItDoes: [
      "Rewrites your LinkedIn headline using the [Target Role] | [Specialization] | [Key Skills] formula that recruiters actively search for.",
      "A good headline is the difference between being invisible to recruiter InMail and getting 5–15 messages per month. The formula is not arbitrary — it's calibrated against how LinkedIn's semantic search ranks profiles.",
      "Run this every time your target role shifts or every 6 months as a refresh.",
    ],
    promptText: `You are a LinkedIn SEO specialist for agentic AI roles. Rewrite my LinkedIn headline to maximize recruiter discoverability.

[PASTE YOUR CURRENT HEADLINE]
[PASTE YOUR TARGET ROLE — be specific, e.g. "Senior Agentic AI Engineer" not just "AI Engineer"]
[PASTE YOUR SPECIALIZATION — e.g. "Multi-agent systems for healthcare", "Production RAG for enterprise"]
[LIST YOUR TOP 3–5 TECHNICAL SKILLS — the ones that appear in every target JD]

## Produce three headline variants following this formula:
Format: [Target Role] | [Specialization] | [Key Skills]

Each variant should:
1. Lead with the EXACT role title a recruiter would search for (literal keyword match)
2. Include a specialization that differentiates you (domain, scale, or unique combination)
3. End with 2–4 hard-skill keywords that appear in target job descriptions
4. Stay under LinkedIn's 220-character headline limit

For agentic AI roles, prioritize these searchable keywords: LangGraph, LangChain, MCP, RAG, multi-agent, agentic, production AI, LLM, Claude, Python, vector DB, Kubernetes, MLOps.

For each variant, explain:
- Why this variant would rank in specific recruiter searches
- What tradeoffs you made (which keywords you included vs. excluded)
- Which audience (hiring manager vs. recruiter vs. peer network) it optimizes for

## Also produce:
RECOMMENDED VARIANT
Pick one variant and explain why it's the strongest fit for my stated target role.

KEYWORDS TO ADD TO SKILLS SECTION
If any critical searchable keywords couldn't fit in the headline, list them — they must appear in the Skills section and About paragraph instead.`,
    inputsRequired: [
      "Your current LinkedIn headline",
      "Your target role (specific title — not 'tech job')",
      "Your specialization or differentiator",
      "3–5 top technical skills",
    ],
    timeEstimate: "5–10 minutes",
    tier: "free",
    agenticAIGuidance:
      "The LinkedIn headline is the single most leveraged 220 characters on your whole profile. For agentic AI specifically, LinkedIn's search semantics favor the exact multi-word strings recruiters type: 'agentic AI engineer', 'LangGraph production', 'multi-agent systems'. Generic terms like 'AI' rank you against millions; specific agentic terms put you in a searchable pool of hundreds.",
  },

  {
    slug: "about-section",
    section: "profile",
    title: "About Section — 3-Paragraph Architecture",
    tagline: "Rewrite the About section around the 3-paragraph formula that converts recruiter previews into profile reads.",
    whatItDoes: [
      "The About section's first 3–4 lines appear in recruiter preview cards BEFORE they open your profile. Those lines must contain your target role keywords + domain expertise or the preview doesn't hook them.",
      "This prompt produces a 3-paragraph structure: The Hook (what you do + keywords), The Story (career transition narrative emphasizing value), The Evidence (specific project outcomes with metrics).",
      "Run this once when you've locked your target role. Refresh quarterly as your project portfolio grows.",
    ],
    promptText: `You are a LinkedIn About section copywriter for agentic AI professionals. Write a 3-paragraph About section that maximizes recruiter conversion.

[PASTE YOUR TARGET ROLE AND SPECIALIZATION]
[PASTE YOUR CURRENT ABOUT SECTION OR WRITE "NONE" IF STARTING FROM SCRATCH]
[LIST YOUR TOP 3 AGENTIC AI PROJECTS — title + one-line description + metric if known]
[LIST ANY NOTABLE CREDENTIALS — publications, patents, certs, conference talks]
[WHO IS YOUR TARGET AUDIENCE — hiring managers, recruiters, peers?]

## Produce:

PARAGRAPH 1 — THE HOOK (3–4 lines)
Must include:
- Target role keywords in the first 150 characters (appears in preview card)
- Your domain of expertise
- One specific scale or impact signal (users, throughput, revenue, scale of deployment)
- No "I am seeking" or objective-style language

PARAGRAPH 2 — THE STORY (4–6 lines)
Your career transition or evolution narrative:
- Where you came from + where you are + what you build now
- Emphasize VALUE delivered, not desperation about seeking
- Bridge prior experience to current agentic AI work credibly
- End with a forward-looking statement about the work you want to do next

PARAGRAPH 3 — THE EVIDENCE (4–6 lines)
Specific project outcomes:
- Name at least 2 shipped agentic AI projects with concrete metrics
- Include one "surprising" or memorable detail that sticks
- Reference the 7-domain framework signals the target role needs (Multi-Agent, Production, Safety, etc.)
- Close with how recruiters or hiring managers can engage

KEYWORDS CHECKLIST
List the top 10 searchable keywords from the target role domain and mark each: Present in About (P) / Added to Skills (S) / Still Missing (M).

TONE CALIBRATION
Describe the voice register this About section targets (technical-peer vs. executive vs. recruiter-friendly) and confirm the copy matches.`,
    inputsRequired: [
      "Your target role + specialization",
      "Your current About section (if any)",
      "Top 3 projects with metrics",
      "Notable credentials",
      "Target audience preference",
    ],
    timeEstimate: "15–20 minutes",
    tier: "all_access",
    agenticAIGuidance:
      "For agentic AI specifically, the Evidence paragraph is the most leveraged. Hiring managers scan for: production deployment (not just projects), named frameworks (LangGraph, MCP, not just 'LLM'), scale signals (tool calls/day, users, token budget), and safety/governance depth. A candidate whose Evidence paragraph hits those four signals converts at dramatically higher rates than one listing features.",
  },

  {
    slug: "skills-sequencing",
    section: "profile",
    title: "Skills Section Sequencing",
    tagline: "Reorder your 50 skills so the 10 that matter for your target role rank first.",
    whatItDoes: [
      "LinkedIn displays only the top 3 skills in preview cards and surfaces the top 10 most prominently on your profile. The order is set by LinkedIn's ranking algorithm — influenced heavily by endorsement count and skill relevance signal.",
      "This prompt identifies which 10 skills should be pinned for your target role and which to remove entirely. A grab-bag of 50 skills dilutes the signal; 10 targeted skills with 10+ endorsements each dominate search.",
      "Run once when target role is locked, then again whenever you add a meaningful new skill.",
    ],
    promptText: `You are a LinkedIn profile optimizer. Sequence my Skills section for maximum recruiter discoverability.

[PASTE THE FULL LIST OF SKILLS CURRENTLY ON YOUR LINKEDIN PROFILE]
[PASTE YOUR TARGET ROLE AND DESIRED SENIORITY]
[PASTE 2–3 TARGET JOB DESCRIPTIONS FOR REFERENCE]

## Produce:

PINNED TOP 3 SKILLS
The three skills that should appear in your preview card. These must be:
1. Present as literal keywords in ALL of the target JDs
2. Defensible in a 5-minute interview probe
3. Backed by real project evidence on your profile

TOP 10 SKILLS (ORDERED)
The next 7 skills to list after the pinned 3. These should complete the story about your specialization — for agentic AI, include: LangGraph/LangChain, MCP, RAG, multi-agent, prompt engineering, Python (async), MLOps (OpenTelemetry, Kubernetes), LLM evaluation, AI safety.

SKILLS TO REMOVE
List skills to delete from your profile with the reason for each:
- Can't be defended in a 5-minute interview
- Outdated or signals legacy focus
- Irrelevant to the target role
- Duplicative or granular (e.g. remove "Python 3.10" if "Python" is listed)

SKILLS TO ADD (IF EARNED)
Based on the target JDs, list skills that should be added to your profile if you have the experience to defend them.

ENDORSEMENT STRATEGY
Which 5 skills should you prioritize getting endorsements on (targeting 10+ endorsements each). Provide 2–3 suggested messages you could send to former colleagues asking for endorsements on specific skills — NOT generic "endorse me" requests.`,
    inputsRequired: [
      "Your full current skills list",
      "Target role + seniority",
      "2–3 reference JDs",
    ],
    timeEstimate: "10–15 minutes",
    tier: "all_access",
    agenticAIGuidance:
      "The #1 mistake candidates make is listing every technology they've ever touched. For agentic AI roles, hiring managers look for depth in the 7 domains, not breadth across 40 tools. A candidate listing 'Python, LangGraph, MCP, OpenTelemetry' with strong endorsements ranks materially higher than one listing 'Python, Java, C++, Scala, R, Go, Rust, JavaScript, TypeScript…' — even if the second has technically more skills.",
  },

  {
    slug: "featured-section",
    section: "profile",
    title: "Featured Section Curator",
    tagline: "Pin the right 3–4 artifacts to the top of your profile.",
    whatItDoes: [
      "The Featured section is prime profile real estate — the first visual element after your About. It should pin artifacts that prove you can do what your target role requires.",
      "This prompt picks the right 3–4 items to feature (from a larger pool of possibilities) and identifies what's missing that you should create to fill remaining slots.",
      "Run after a major project ships or when your target role shifts.",
    ],
    promptText: `You are a LinkedIn profile curator. Identify the optimal 3–4 items for my Featured section.

[PASTE YOUR TARGET ROLE AND SPECIALIZATION]
[LIST ALL PUBLIC ARTIFACTS YOU HAVE: GitHub repos, blog posts, talks, papers, demos, projects, articles]
[FOR EACH: TITLE, URL, PUBLICATION DATE, BRIEF DESCRIPTION]

## Produce:

RECOMMENDED FEATURED LINEUP (3–4 items)
For each recommended item:
- The artifact name and what it proves
- Why this item belongs in Featured for the target role
- What order it should appear in (left to right)
- What caption to add (one sentence positioning the artifact)

ITEMS TO CUT
Artifacts that shouldn't be featured, with the reason (e.g., outdated, off-target, weaker than alternatives, dilutes positioning).

FEATURED CONTENT GAPS
Based on the target role, what specific artifacts are missing that you should create? Examples:
- A technical blog post on a specific agentic AI pattern (e.g., "How I built production HITL for my agent")
- A GitHub repo with a documented agentic AI project
- A conference talk or meetup presentation recording
- A case study from a real production deployment
- An evaluation or benchmark published openly

For each gap, suggest:
- A specific title that would be searchable
- An estimated time to produce
- Why this artifact would materially move your candidacy

PROMOTIONAL STRATEGY
For each pinned Featured item, suggest one post you could publish to drive engagement to the artifact — turning passive Featured content into active signal.`,
    inputsRequired: [
      "Your target role",
      "Complete list of your public artifacts",
    ],
    timeEstimate: "15–20 minutes",
    tier: "all_access",
    agenticAIGuidance:
      "For agentic AI roles in 2026, the highest-signal Featured item you can create is a documented GitHub repo with a shipped multi-agent system (using LangGraph or a comparable framework) — with a README that explains the architecture decisions, the observability setup, and at least one production lesson learned. This single artifact often closes the 'did you actually ship' gap that hiring managers are trying to verify.",
  },
  ],
  es: [
  {
    "slug": "headline-rewriter",
    "section": "profile",
    "title": "Reescritor de Titular",
    "tagline": "Convierte un titular invisible en un imán amigable para las búsquedas de reclutadores.",
    "whatItDoes": [
      "Reescribe tu titular de LinkedIn usando la fórmula [Rol Objetivo] | [Especialización] | [Aptitudes Clave] que los reclutadores buscan activamente.",
      "Un buen titular marca la diferencia entre ser invisible para el InMail de reclutadores y recibir de 5 a 15 mensajes por mes. La fórmula no es arbitraria: está calibrada según cómo la búsqueda semántica de LinkedIn clasifica los perfiles.",
      "Ejecuta esto cada vez que cambie tu rol objetivo o cada 6 meses como actualización."
    ],
    "promptText": "Eres un especialista en SEO de LinkedIn para roles de agentic AI. Reescribe mi titular de LinkedIn para maximizar la visibilidad ante los reclutadores.\n\n[PEGA TU TITULAR ACTUAL]\n[PEGA TU ROL OBJETIVO — sé específico, p. ej. \"Senior Agentic AI Engineer\" y no solo \"AI Engineer\"]\n[PEGA TU ESPECIALIZACIÓN — p. ej. \"Sistemas multiagente para salud\", \"RAG en producción para empresas\"]\n[ENUMERA TUS 3–5 PRINCIPALES APTITUDES TÉCNICAS — las que aparecen en cada descripción de puesto objetivo]\n\n## Produce tres variantes de titular siguiendo esta fórmula:\nFormato: [Rol Objetivo] | [Especialización] | [Aptitudes Clave]\n\nCada variante debe:\n1. Comenzar con el título de rol EXACTO que un reclutador buscaría (coincidencia literal de palabra clave)\n2. Incluir una especialización que te diferencie (dominio, escala o combinación única)\n3. Terminar con 2–4 palabras clave de aptitudes técnicas que aparecen en las descripciones de puestos objetivo\n4. Mantenerse por debajo del límite de 220 caracteres del titular de LinkedIn\n\nPara roles de agentic AI, prioriza estas palabras clave buscables: LangGraph, LangChain, MCP, RAG, multiagente, agentic, AI en producción, LLM, Claude, Python, base de datos vectorial, Kubernetes, MLOps.\n\nPara cada variante, explica:\n- Por qué esta variante se clasificaría en búsquedas específicas de reclutadores\n- Qué compensaciones hiciste (qué palabras clave incluiste vs. excluiste)\n- Para qué audiencia (responsable de contratación vs. reclutador vs. red de colegas) está optimizada\n\n## Produce también:\nVARIANTE RECOMENDADA\nElige una variante y explica por qué es la más adecuada para mi rol objetivo declarado.\n\nPALABRAS CLAVE PARA AGREGAR A LA SECCIÓN DE APTITUDES\nSi alguna palabra clave buscable crítica no cupo en el titular, enumérala: debe aparecer en la sección de Aptitudes y en el párrafo Acerca de en su lugar.",
    "inputsRequired": [
      "Tu titular actual de LinkedIn",
      "Tu rol objetivo (título específico — no 'trabajo en tecnología')",
      "Tu especialización o diferenciador",
      "3–5 principales aptitudes técnicas"
    ],
    "timeEstimate": "5–10 minutos",
    "tier": "free",
    "agenticAIGuidance": "El titular de LinkedIn son los 220 caracteres con mayor apalancamiento de todo tu perfil. Para agentic AI específicamente, la semántica de búsqueda de LinkedIn favorece las cadenas exactas de varias palabras que los reclutadores escriben: 'agentic AI engineer', 'LangGraph production', 'sistemas multiagente'. Los términos genéricos como 'AI' te clasifican frente a millones; los términos específicos de agentic te colocan en un grupo buscable de cientos."
  },
  {
    "slug": "about-section",
    "section": "profile",
    "title": "Sección Acerca de — Arquitectura de 3 Párrafos",
    "tagline": "Reescribe la sección Acerca de en torno a la fórmula de 3 párrafos que convierte las vistas previas de reclutadores en lecturas de perfil.",
    "whatItDoes": [
      "Las primeras 3–4 líneas de la sección Acerca de aparecen en las tarjetas de vista previa de los reclutadores ANTES de que abran tu perfil. Esas líneas deben contener las palabras clave de tu rol objetivo + tu experiencia de dominio, o la vista previa no los engancha.",
      "Este prompt produce una estructura de 3 párrafos: El Gancho (qué haces + palabras clave), La Historia (narrativa de transición profesional que enfatiza el valor), La Evidencia (resultados específicos de proyectos con métricas).",
      "Ejecuta esto una vez cuando hayas fijado tu rol objetivo. Actualiza trimestralmente a medida que crezca tu portafolio de proyectos."
    ],
    "promptText": "Eres un redactor de la sección Acerca de de LinkedIn para profesionales de agentic AI. Escribe una sección Acerca de de 3 párrafos que maximice la conversión de reclutadores.\n\n[PEGA TU ROL OBJETIVO Y ESPECIALIZACIÓN]\n[PEGA TU SECCIÓN ACERCA DE ACTUAL O ESCRIBE \"NINGUNA\" SI EMPIEZAS DESDE CERO]\n[ENUMERA TUS 3 PRINCIPALES PROYECTOS DE AGENTIC AI — título + descripción de una línea + métrica si la conoces]\n[ENUMERA CUALQUIER CREDENCIAL NOTABLE — publicaciones, patentes, certificaciones, charlas en conferencias]\n[¿QUIÉN ES TU AUDIENCIA OBJETIVO — responsables de contratación, reclutadores, colegas?]\n\n## Produce:\n\nPÁRRAFO 1 — EL GANCHO (3–4 líneas)\nDebe incluir:\n- Palabras clave del rol objetivo en los primeros 150 caracteres (aparece en la tarjeta de vista previa)\n- Tu dominio de experiencia\n- Una señal específica de escala o impacto (usuarios, rendimiento, ingresos, escala de despliegue)\n- Nada de lenguaje tipo \"estoy buscando\" o de objetivo\n\nPÁRRAFO 2 — LA HISTORIA (4–6 líneas)\nTu narrativa de transición o evolución profesional:\n- De dónde vienes + dónde estás + qué construyes ahora\n- Enfatiza el VALOR entregado, no la desesperación por buscar\n- Conecta creíblemente tu experiencia previa con tu trabajo actual en agentic AI\n- Termina con una declaración prospectiva sobre el trabajo que quieres hacer a continuación\n\nPÁRRAFO 3 — LA EVIDENCIA (4–6 líneas)\nResultados específicos de proyectos:\n- Nombra al menos 2 proyectos de agentic AI lanzados con métricas concretas\n- Incluye un detalle \"sorprendente\" o memorable que se quede grabado\n- Haz referencia a las señales del framework de 7 dominios que requiere el rol objetivo (Multiagente, Producción, Seguridad, etc.)\n- Cierra con cómo pueden interactuar contigo los reclutadores o responsables de contratación\n\nLISTA DE VERIFICACIÓN DE PALABRAS CLAVE\nEnumera las 10 principales palabras clave buscables del dominio del rol objetivo y marca cada una: Presente en Acerca de (P) / Agregada a Aptitudes (S) / Aún Falta (M).\n\nCALIBRACIÓN DE TONO\nDescribe el registro de voz al que apunta esta sección Acerca de (par técnico vs. ejecutivo vs. amigable para reclutadores) y confirma que el texto coincide.",
    "inputsRequired": [
      "Tu rol objetivo + especialización",
      "Tu sección Acerca de actual (si la hay)",
      "3 principales proyectos con métricas",
      "Credenciales notables",
      "Preferencia de audiencia objetivo"
    ],
    "timeEstimate": "15–20 minutos",
    "tier": "all_access",
    "agenticAIGuidance": "Para agentic AI específicamente, el párrafo de Evidencia es el de mayor apalancamiento. Los responsables de contratación escanean en busca de: despliegue en producción (no solo proyectos), frameworks nombrados (LangGraph, MCP, no solo 'LLM'), señales de escala (llamadas a herramientas/día, usuarios, presupuesto de tokens) y profundidad en seguridad/gobernanza. Un candidato cuyo párrafo de Evidencia toca esas cuatro señales convierte a tasas dramáticamente más altas que uno que enumera funcionalidades."
  },
  {
    "slug": "skills-sequencing",
    "section": "profile",
    "title": "Secuenciación de la Sección de Aptitudes",
    "tagline": "Reordena tus 50 aptitudes para que las 10 que importan para tu rol objetivo aparezcan primero.",
    "whatItDoes": [
      "LinkedIn muestra solo las 3 principales aptitudes en las tarjetas de vista previa y destaca las 10 principales de forma más prominente en tu perfil. El orden lo establece el algoritmo de clasificación de LinkedIn, influenciado en gran medida por la cantidad de recomendaciones y la señal de relevancia de la habilidad.",
      "Este prompt identifica qué 10 aptitudes deberían fijarse para tu rol objetivo y cuáles eliminar por completo. Una mezcla de 50 aptitudes diluye la señal; 10 aptitudes específicas con 10+ recomendaciones cada una dominan la búsqueda.",
      "Ejecuta esto una vez cuando el rol objetivo esté fijado, y de nuevo cada vez que agregues una nueva habilidad significativa."
    ],
    "promptText": "Eres un optimizador de perfiles de LinkedIn. Secuencia mi sección de Aptitudes para máxima visibilidad ante reclutadores.\n\n[PEGA LA LISTA COMPLETA DE APTITUDES QUE ACTUALMENTE ESTÁN EN TU PERFIL DE LINKEDIN]\n[PEGA TU ROL OBJETIVO Y NIVEL DE SENIORITY DESEADO]\n[PEGA 2–3 DESCRIPCIONES DE PUESTOS OBJETIVO COMO REFERENCIA]\n\n## Produce:\n\n3 APTITUDES PRINCIPALES FIJADAS\nLas tres aptitudes que deberían aparecer en tu tarjeta de vista previa. Estas deben:\n1. Estar presentes como palabras clave literales en TODAS las descripciones de puestos objetivo\n2. Ser defendibles en un sondeo de entrevista de 5 minutos\n3. Estar respaldadas por evidencia real de proyectos en tu perfil\n\n10 PRINCIPALES APTITUDES (ORDENADAS)\nLas siguientes 7 aptitudes a listar después de las 3 fijadas. Estas deberían completar la historia sobre tu especialización — para agentic AI, incluye: LangGraph/LangChain, MCP, RAG, multiagente, ingeniería de prompts, Python (async), MLOps (OpenTelemetry, Kubernetes), evaluación de LLM, seguridad de AI.\n\nAPTITUDES A ELIMINAR\nEnumera las aptitudes a borrar de tu perfil con el motivo de cada una:\n- No se pueden defender en un sondeo de entrevista de 5 minutos\n- Desactualizadas o que señalan un enfoque heredado\n- Irrelevantes para el rol objetivo\n- Duplicadas o demasiado granulares (p. ej., elimina \"Python 3.10\" si \"Python\" está listado)\n\nAPTITUDES A AGREGAR (SI LAS HAS GANADO)\nSegún las descripciones de puestos objetivo, enumera las aptitudes que deberían agregarse a tu perfil si tienes la experiencia para defenderlas.\n\nESTRATEGIA DE RECOMENDACIONES\nEn qué 5 aptitudes deberías priorizar obtener recomendaciones (apuntando a 10+ recomendaciones cada una). Proporciona 2–3 mensajes sugeridos que podrías enviar a excolegas pidiendo recomendaciones sobre aptitudes específicas — NO solicitudes genéricas de \"recomiéndame\".",
    "inputsRequired": [
      "Tu lista completa de aptitudes actuales",
      "Rol objetivo + seniority",
      "2–3 descripciones de puestos de referencia"
    ],
    "timeEstimate": "10–15 minutos",
    "tier": "all_access",
    "agenticAIGuidance": "El error #1 que cometen los candidatos es listar cada tecnología que hayan tocado alguna vez. Para roles de agentic AI, los responsables de contratación buscan profundidad en los 7 dominios, no amplitud a través de 40 herramientas. Un candidato que lista 'Python, LangGraph, MCP, OpenTelemetry' con recomendaciones sólidas se clasifica materialmente más alto que uno que lista 'Python, Java, C++, Scala, R, Go, Rust, JavaScript, TypeScript…' — aun si el segundo tiene técnicamente más aptitudes."
  },
  {
    "slug": "featured-section",
    "section": "profile",
    "title": "Curador de la Sección Destacado",
    "tagline": "Fija los 3–4 artefactos correctos en la parte superior de tu perfil.",
    "whatItDoes": [
      "La sección Destacado es un espacio privilegiado del perfil — el primer elemento visual después de tu sección Acerca de. Debería fijar artefactos que demuestren que puedes hacer lo que requiere tu rol objetivo.",
      "Este prompt elige los 3–4 elementos correctos para destacar (de un grupo mayor de posibilidades) e identifica qué falta que deberías crear para llenar los espacios restantes.",
      "Ejecuta esto después de lanzar un proyecto importante o cuando cambie tu rol objetivo."
    ],
    "promptText": "Eres un curador de perfiles de LinkedIn. Identifica los 3–4 elementos óptimos para mi sección Destacado.\n\n[PEGA TU ROL OBJETIVO Y ESPECIALIZACIÓN]\n[ENUMERA TODOS LOS ARTEFACTOS PÚBLICOS QUE TIENES: repositorios de GitHub, publicaciones de blog, charlas, artículos científicos, demos, proyectos, artículos]\n[PARA CADA UNO: TÍTULO, URL, FECHA DE PUBLICACIÓN, BREVE DESCRIPCIÓN]\n\n## Produce:\n\nSELECCIÓN DESTACADA RECOMENDADA (3–4 elementos)\nPara cada elemento recomendado:\n- El nombre del artefacto y qué demuestra\n- Por qué este elemento pertenece a Destacado para el rol objetivo\n- En qué orden debería aparecer (de izquierda a derecha)\n- Qué leyenda agregar (una frase que posicione el artefacto)\n\nELEMENTOS A DESCARTAR\nArtefactos que no deberían destacarse, con el motivo (p. ej., desactualizados, fuera de objetivo, más débiles que las alternativas, diluyen el posicionamiento).\n\nBRECHAS DE CONTENIDO DESTACADO\nSegún el rol objetivo, ¿qué artefactos específicos faltan que deberías crear? Ejemplos:\n- Una publicación técnica de blog sobre un patrón específico de agentic AI (p. ej., \"Cómo construí HITL en producción para mi agente\")\n- Un repositorio de GitHub con un proyecto de agentic AI documentado\n- Una grabación de una charla en conferencia o presentación en un meetup\n- Un caso de estudio de un despliegue real en producción\n- Una evaluación o benchmark publicado abiertamente\n\nPara cada brecha, sugiere:\n- Un título específico que sea buscable\n- Un tiempo estimado para producirlo\n- Por qué este artefacto haría avanzar materialmente tu candidatura\n\nESTRATEGIA PROMOCIONAL\nPara cada elemento Destacado fijado, sugiere una publicación que podrías compartir para generar interacción hacia el artefacto — convirtiendo el contenido Destacado pasivo en señal activa.",
    "inputsRequired": [
      "Tu rol objetivo",
      "Lista completa de tus artefactos públicos"
    ],
    "timeEstimate": "15–20 minutos",
    "tier": "all_access",
    "agenticAIGuidance": "Para roles de agentic AI en 2026, el elemento Destacado de mayor señal que puedes crear es un repositorio de GitHub documentado con un sistema multiagente lanzado (usando LangGraph o un framework comparable) — con un README que explique las decisiones de arquitectura, la configuración de observabilidad y al menos una lección aprendida en producción. Este único artefacto a menudo cierra la brecha de '¿realmente lo lanzaste?' que los responsables de contratación intentan verificar."
  }
],
};

// ---------------------------------------------------------------------------
// CONTENT STRATEGY — Post archetypes + writing prompts
// ---------------------------------------------------------------------------

export const POST_ARCHETYPES: Localized<PostArchetype[]> = {
  en: [

  {
    slug: "news-commentary",
    name: "News Commentary",
    purpose: "React to breaking news in agentic AI with a differentiated perspective. Establishes you as current and opinionated.",
    idealFrequency: "3–4 per week",
    structure: [
      "HOOK — the news event (1 line, specific)",
      "PATTERN — the architectural/market shift underneath it (2–3 lines)",
      "PERSONAL CONNECTION — a lived experience that shows you already navigated this pattern (3–5 lines with specific detail)",
      "STAKES — what it means for teams making decisions today (2–3 lines, opinionated)",
      "INVITATION — a question that invites engagement",
    ],
    hookFormulas: [
      "[Company/Technology] just [action]. Let that sink in.",
      "[Surprising stat from the news]. Here's what most commentary misses.",
      "The [news event] tells you less about [surface topic] than about [deeper pattern].",
      "Everyone is talking about [news]. The more interesting question is [deeper question].",
    ],
    cta: "Ask a question that invites readers to share their team's current approach — drives comments and surfaces engagement signal to the LinkedIn algorithm.",
    exemplar: {
      headline: "News Commentary — Protocol standardization",
      body: `MCP just crossed 97 million installs. Let that sink in.

Six months ago, most enterprise teams I talked to were still debating whether AI agents were "real." Today, the Model Context Protocol has become the TCP/IP of agentic infrastructure. It's not experimental anymore. It's foundational.

Here's what most commentary misses: the real story isn't the install count. It's the architectural shift underneath it.

When I built a production agentic evaluation system — FastAPI backend, LLM scoring pipeline, 100+ live sessions per week — the hardest problem wasn't the model. It was connecting the agent to everything else: transcription services, knowledge bases, human review workflows, production databases.

MCP solves exactly this. It standardizes how agents talk to tools, data, and systems. That's why the Agentic AI Foundation formed under the Linux Foundation with contributions from Anthropic, OpenAI, and Block. The competitive walls are coming down at the infrastructure layer.

The $7.51 billion agentic AI market isn't growing because models got smarter. It's growing because the plumbing finally works.

If your team is still building custom agent-to-tool integrations from scratch, you're already behind. The companies shipping fastest in 2026 are the ones building on standardized agent protocols — not reinventing them.

What's your team's agent infrastructure strategy? Still custom, or moving to standards?

#AgenticAI #MCP #ArtificialIntelligence #AIAgents #ProductionAI`,
      whyItWorks: [
        "Opens with a verifiable stat (97M installs) — grounds the post in specificity, not vibes",
        "Pivots from surface ('install count') to insight ('architectural shift') — signals the author is thinking one layer deeper than the newsfeed",
        "Personal connection includes specific scale + specific tech stack — proves the author actually built something in this space",
        "Closes with a pointed claim + an invitation — creates the opportunity for comments without being preachy",
      ],
    },
    tier: "free",
  },

  {
    slug: "experience-insight",
    name: "Experience / Insight",
    purpose: "Share a production lesson drawn from your real work. Establishes practitioner credibility.",
    idealFrequency: "2–3 per week",
    structure: [
      "HOOK — a stat or claim that contradicts common belief (1 line)",
      "PERSONAL ANCHOR — 'I lived this problem firsthand' (1 line)",
      "SETUP — what you were building, at what scale (2–3 lines with specific numbers)",
      "THE HUMBLING MOMENT — what failed in production vs. testing (2–3 lines)",
      "LESSONS — 3–5 numbered bullets, each with a specific principle",
      "BROADER CLAIM — what this means for the market in 2026 (2 lines)",
      "QUESTION — invite engagement",
    ],
    hookFormulas: [
      "[Surprising stat]. But here's the number that matters more: [second stat].",
      "[X%] of teams [common claim]. In my experience, only [Y%] actually [deeper truth].",
      "The thing nobody tells you about [production AI topic]:",
      "I've deployed [N] agent systems to production. Here's what I wish I'd known at #1.",
    ],
    cta: "Ask about the reader's biggest challenge in the specific area you discussed.",
    exemplar: {
      headline: "Experience / Insight — Production quality infrastructure",
      body: `57% of organizations now have AI agents in production. But here's the number that matters more: 32% say quality is their #1 barrier.

I've lived this problem firsthand.

When I designed an agentic evaluation system processing 100+ live sessions weekly, the model worked great in testing. In production? The first week was humbling. Edge cases everywhere. Scoring inconsistencies. Hallucinated feedback that sounded authoritative but was wrong.

The fix wasn't a better model. It was a better evaluation framework.

Here's what I learned building production-grade agent quality systems:

1. You need structured scoring rubrics BEFORE you deploy — not after users complain
2. Human-in-the-loop isn't a crutch. It's architecture. Build quality gates at every decision point
3. The feedback loop matters more than the model. 22% performance improvement came from continuous measurement, not a model upgrade
4. Agent evaluation is its own engineering discipline. Treat it that way

After 25 years shipping AI systems, I can tell you: the gap between demo and production has never been wider than it is with agents.

The organizations that win in 2026 won't have the best models. They'll have the best evaluation and quality infrastructure around their models.

What's your biggest challenge getting agents from prototype to production?

#AgenticAI #GenerativeAI #MLOps #ProductionAI #AIStrategy`,
      whyItWorks: [
        "Leads with a stat that hooks (57%) and pivots to the more interesting stat (32% quality barrier) — signals the author reads the whole industry report, not the headline",
        "'The first week was humbling' — vulnerability that practitioners recognize and readers trust",
        "Four lessons are structurally parallel and specific — they are not generic 'build quality in from the start' platitudes",
        "Closes with a bold claim ('best evaluation infrastructure, not best models') — invites disagreement which drives engagement",
      ],
    },
    tier: "all_access",
  },

  {
    slug: "educational-technical",
    name: "Educational / Technical",
    purpose: "Teach a concept, framework, or pattern. Establishes you as a generous contributor to the field.",
    idealFrequency: "2–3 per week",
    structure: [
      "HOOK — the question the post will answer (1 line)",
      "WHY THIS MATTERS — production cost of getting this wrong (2 lines)",
      "THE FRAMEWORK — 3–5 ordered steps or principles",
      "CONCRETE EXAMPLE — applied to a specific scenario (3–4 lines)",
      "NUANCE — what this DOESN'T solve (2 lines — builds credibility)",
      "NEXT STEPS — what a reader should do tomorrow",
      "OPEN INVITATION — what the reader might add",
    ],
    hookFormulas: [
      "Most teams get [specific technical topic] wrong. Here's the framework I use:",
      "[Technical concept] is the thing that separates Senior from Staff. Here's how to think about it:",
      "If you're building [agentic AI thing], you need to decide [specific decision] before you write a line of code. Here's the decision tree:",
      "I've designed [N] [pattern] in production. Here are the [M] rules I'd never break again:",
    ],
    cta: "Invite readers to share their own rules / counterexamples / additions.",
    exemplar: {
      headline: "Educational / Technical — A framework post",
      body: `Most teams designing multi-agent systems skip the one question that determines whether the system works in production: who owns the decision about what happens next?

This is the orchestrator vs. executor distinction, and most resumes collapse it into 'I built a multi-agent system' — which tells a hiring manager nothing.

Here's how I teach it:

1. The ORCHESTRATOR decides. It holds the goal, reasons about what to do next, routes work. It's the control plane.

2. The EXECUTOR does. It calls the tool, runs the agent step, executes the decision. It's the data plane.

These are different concerns with different reliability requirements. An orchestrator failure stops the workflow; an executor failure might be retryable. When you keep these concerns separate, you can add fault tolerance at the right layer.

Concrete example: a supervisor-topology document processing system I worked on routed 50K documents per day across 16 specialist agents. The supervisor (orchestrator) did lightweight classification and routing. Each specialist (executor) did deep analysis in its own domain.

When a specialist crashed, the supervisor routed that document to a backup specialist or to human review. When the supervisor itself hit trouble, the whole pipeline paused — which was the correct failure mode given our compliance requirements.

What this does NOT solve: emergent coordination problems between specialists. If two executors need to agree on shared state, you need inter-agent messaging as a separate concern.

The rule: when you describe a multi-agent system, name the orchestrator and the executors explicitly. That single distinction signals to any interviewer that you've actually built one.

What's one multi-agent system distinction you wish more people understood?

#AgenticAI #SoftwareArchitecture #LangGraph #AIAgents #MLOps`,
      whyItWorks: [
        "Opens by calling out the specific mistake most teams make — establishes the author is distinguishing signal from noise",
        "Numbered framework (1, 2) is scannable — LinkedIn readers skim, then stop on structure",
        "Concrete example with scale numbers (50K docs/day, 16 agents) — converts 'framework post' from theory to practice",
        "Explicit 'what this does NOT solve' — builds credibility by not overclaiming, a move that cheap content creators skip",
      ],
    },
    tier: "all_access",
  },

  {
    slug: "engagement-prompt",
    name: "Engagement Prompt",
    purpose: "Drive comments through a question, hot take, or poll. Signals active engagement to LinkedIn's algorithm and surfaces your audience's thinking.",
    idealFrequency: "1–2 per week",
    structure: [
      "HOOK — the polarizing or non-obvious question (1–2 lines)",
      "YOUR STAKE IN THE GROUND — a short opinion that invites disagreement (2–3 lines)",
      "THE FRAMEWORK YOU'D WANT THE ANSWERS TO USE — show you've thought structurally about the question",
      "OPEN INVITATION — ask readers to weigh in",
    ],
    hookFormulas: [
      "Unpopular opinion: [provocative claim about AI/hiring/work]",
      "Question for the agentic AI builders: [specific decision point]",
      "If you had to choose between [A] and [B] in your next agentic system, which would you pick?",
      "Everyone says [common wisdom]. Here's why I think that's wrong:",
    ],
    cta: "The entire post is the CTA — expect comments, not just likes.",
    exemplar: {
      headline: "Engagement Prompt — Opinionated question",
      body: `Unpopular opinion: 'prompt engineering' is not an engineering discipline yet.

I know that's heretical in a market with 'Prompt Engineer' job titles paying $250K. But here's what I actually see in production agentic systems:

Most 'prompt engineering' today is: write a prompt → try it → see if it works → adjust text until it works.

That's not engineering. That's craft.

Engineering disciplines have:
- Versioned artifacts with rollback capability
- Testing gates (not just vibes)
- Quantitative quality measurement
- Documented failure modes
- Separation of concerns

Prompt engineering today has none of those systematically. The prompts live in code comments, change without tests, break without monitoring, and are maintained by tribal knowledge.

The teams that are winning in production agentic AI are the ones building these missing pieces — prompt versioning, golden test sets, drift detection, A/B frameworks. They're not 'prompt engineers.' They're engineers who happen to write prompts, and who built the discipline around it.

Is prompt engineering a real engineering discipline yet in your org — or is it still craft masquerading as engineering?

#AgenticAI #PromptEngineering #MLOps #AIEngineering`,
      whyItWorks: [
        "Lead with 'Unpopular opinion' — LinkedIn readers scroll past consensus and stop on controversy",
        "Backs the opinion with specific criteria (versioned artifacts, testing gates, etc.) — not just 'vibes'",
        "Doesn't insult prompt engineers — distinguishes 'the people' from 'the discipline's maturity'",
        "Closes with a question that forces readers to take a position in their own reply — comment-drivers, not like-drivers",
      ],
    },
    tier: "all_access",
  },
  ],
  es: [
  {
    "slug": "news-commentary",
    "name": "Comentario de Noticias",
    "purpose": "Reacciona ante noticias de última hora sobre agentic AI con una perspectiva diferenciada. Te posiciona como una persona actualizada y con opinión propia.",
    "idealFrequency": "3–4 por semana",
    "structure": [
      "GANCHO — el hecho noticioso (1 línea, específico)",
      "PATRÓN — el cambio arquitectónico/de mercado que subyace (2–3 líneas)",
      "CONEXIÓN PERSONAL — una experiencia vivida que muestre que ya navegaste este patrón (3–5 líneas con detalle específico)",
      "LO QUE ESTÁ EN JUEGO — qué significa para los equipos que toman decisiones hoy (2–3 líneas, con opinión)",
      "INVITACIÓN — una pregunta que invite a la interacción"
    ],
    "hookFormulas": [
      "[Empresa/Tecnología] acaba de [acción]. Deja que eso cale.",
      "[Dato sorprendente de la noticia]. Esto es lo que la mayoría de los comentarios pasa por alto.",
      "El [hecho noticioso] te dice menos sobre [tema superficial] que sobre [patrón más profundo].",
      "Todos hablan de [noticia]. La pregunta más interesante es [pregunta más profunda]."
    ],
    "cta": "Haz una pregunta que invite a los lectores a compartir el enfoque actual de su equipo: genera comentarios y hace aflorar señales de interacción para el algoritmo de LinkedIn.",
    "exemplar": {
      "headline": "Comentario de Noticias — Estandarización de protocolos",
      "body": "MCP acaba de superar los 97 millones de instalaciones. Deja que eso cale.\n\nHace seis meses, la mayoría de los equipos empresariales con los que hablaba todavía debatían si los agentes de IA eran \"reales\". Hoy, el Model Context Protocol se ha convertido en el TCP/IP de la infraestructura agéntica. Ya no es experimental. Es fundamental.\n\nEsto es lo que la mayoría de los comentarios pasa por alto: la verdadera historia no es la cantidad de instalaciones. Es el cambio arquitectónico que subyace.\n\nCuando construí un sistema de evaluación agéntica en producción —backend en FastAPI, pipeline de scoring con LLM, más de 100 sesiones en vivo por semana—, el problema más difícil no era el modelo. Era conectar el agente con todo lo demás: servicios de transcripción, bases de conocimiento, flujos de revisión humana, bases de datos en producción.\n\nMCP resuelve exactamente esto. Estandariza cómo los agentes se comunican con herramientas, datos y sistemas. Por eso la Agentic AI Foundation se formó bajo la Linux Foundation con contribuciones de Anthropic, OpenAI y Block. Los muros competitivos están cayendo en la capa de infraestructura.\n\nEl mercado de agentic AI de 7.51 mil millones de dólares no está creciendo porque los modelos se hayan vuelto más inteligentes. Está creciendo porque la fontanería por fin funciona.\n\nSi tu equipo todavía construye integraciones personalizadas de agente a herramienta desde cero, ya estás rezagado. Las empresas que entregan más rápido en 2026 son las que construyen sobre protocolos de agente estandarizados, no las que los reinventan.\n\n¿Cuál es la estrategia de infraestructura de agentes de tu equipo? ¿Todavía personalizada o migrando hacia estándares?\n\n#AgenticAI #MCP #ArtificialIntelligence #AIAgents #ProductionAI",
      "whyItWorks": [
        "Abre con un dato verificable (97M de instalaciones): ancla la publicación en la especificidad, no en impresiones vagas",
        "Pasa de la superficie ('cantidad de instalaciones') al insight ('cambio arquitectónico'): señala que el autor piensa una capa más profundo que el feed de noticias",
        "La conexión personal incluye una escala específica + un stack tecnológico específico: demuestra que el autor realmente construyó algo en este espacio",
        "Cierra con una afirmación puntual + una invitación: crea la oportunidad de comentarios sin resultar sermoneador"
      ]
    },
    "tier": "free"
  },
  {
    "slug": "experience-insight",
    "name": "Experiencia / Perspectiva",
    "purpose": "Comparte una lección de producción extraída de tu trabajo real. Establece credibilidad como profesional en ejercicio.",
    "idealFrequency": "2–3 por semana",
    "structure": [
      "GANCHO — un dato o afirmación que contradiga la creencia común (1 línea)",
      "ANCLA PERSONAL — 'Viví este problema en carne propia' (1 línea)",
      "PLANTEAMIENTO — qué estabas construyendo, a qué escala (2–3 líneas con números específicos)",
      "EL MOMENTO DE HUMILDAD — qué falló en producción frente a las pruebas (2–3 líneas)",
      "LECCIONES — 3–5 viñetas numeradas, cada una con un principio específico",
      "AFIRMACIÓN MÁS AMPLIA — qué significa esto para el mercado en 2026 (2 líneas)",
      "PREGUNTA — invita a la interacción"
    ],
    "hookFormulas": [
      "[Dato sorprendente]. Pero este es el número que importa más: [segundo dato].",
      "[X%] de los equipos [afirmación común]. En mi experiencia, solo [Y%] realmente [verdad más profunda].",
      "Lo que nadie te cuenta sobre [tema de IA en producción]:",
      "He desplegado [N] sistemas de agentes en producción. Esto es lo que ojalá hubiera sabido en el #1."
    ],
    "cta": "Pregunta cuál es el mayor desafío del lector en el área específica que abordaste.",
    "exemplar": {
      "headline": "Experiencia / Perspectiva — Infraestructura de calidad en producción",
      "body": "El 57% de las organizaciones ya tienen agentes de IA en producción. Pero este es el número que importa más: el 32% dice que la calidad es su barrera #1.\n\nViví este problema en carne propia.\n\nCuando diseñé un sistema de evaluación agéntica que procesaba más de 100 sesiones en vivo por semana, el modelo funcionaba de maravilla en las pruebas. ¿En producción? La primera semana fue una lección de humildad. Casos límite por todas partes. Inconsistencias en el scoring. Retroalimentación alucinada que sonaba autoritativa pero estaba equivocada.\n\nLa solución no fue un mejor modelo. Fue un mejor framework de evaluación.\n\nEsto es lo que aprendí al construir sistemas de calidad de agentes de nivel de producción:\n\n1. Necesitas rúbricas de scoring estructuradas ANTES de desplegar, no después de que los usuarios se quejen\n2. El human-in-the-loop no es una muleta. Es arquitectura. Construye compuertas de calidad en cada punto de decisión\n3. El bucle de retroalimentación importa más que el modelo. Un 22% de mejora en el rendimiento vino de la medición continua, no de una actualización del modelo\n4. La evaluación de agentes es su propia disciplina de ingeniería. Trátala como tal\n\nDespués de 25 años entregando sistemas de IA, puedo decirte esto: la brecha entre la demo y la producción nunca ha sido tan amplia como lo es con los agentes.\n\nLas organizaciones que ganen en 2026 no tendrán los mejores modelos. Tendrán la mejor infraestructura de evaluación y calidad en torno a sus modelos.\n\n¿Cuál es tu mayor desafío para llevar agentes del prototipo a la producción?\n\n#AgenticAI #GenerativeAI #MLOps #ProductionAI #AIStrategy",
      "whyItWorks": [
        "Empieza con un dato que engancha (57%) y pasa al dato más interesante (32% de barrera de calidad): señala que el autor lee el informe completo de la industria, no el titular",
        "'La primera semana fue una lección de humildad': vulnerabilidad que los profesionales reconocen y en la que los lectores confían",
        "Las cuatro lecciones son estructuralmente paralelas y específicas: no son perogrulladas genéricas del tipo 'incorpora la calidad desde el principio'",
        "Cierra con una afirmación audaz ('la mejor infraestructura de evaluación, no los mejores modelos'): invita al desacuerdo, lo que impulsa la interacción"
      ]
    },
    "tier": "all_access"
  },
  {
    "slug": "educational-technical",
    "name": "Educativo / Técnico",
    "purpose": "Enseña un concepto, framework o patrón. Te posiciona como alguien que contribuye generosamente al campo.",
    "idealFrequency": "2–3 por semana",
    "structure": [
      "GANCHO — la pregunta que responderá la publicación (1 línea)",
      "POR QUÉ IMPORTA — el costo en producción de hacer esto mal (2 líneas)",
      "EL FRAMEWORK — 3–5 pasos o principios ordenados",
      "EJEMPLO CONCRETO — aplicado a un escenario específico (3–4 líneas)",
      "MATIZ — qué NO resuelve esto (2 líneas: genera credibilidad)",
      "PRÓXIMOS PASOS — qué debería hacer un lector mañana",
      "INVITACIÓN ABIERTA — qué podría aportar el lector"
    ],
    "hookFormulas": [
      "La mayoría de los equipos entiende mal [tema técnico específico]. Este es el framework que uso:",
      "[Concepto técnico] es lo que separa a un Senior de un Staff. Así es como conviene pensarlo:",
      "Si estás construyendo [algo de agentic AI], necesitas decidir [decisión específica] antes de escribir una sola línea de código. Este es el árbol de decisión:",
      "He diseñado [N] [patrón] en producción. Estas son las [M] reglas que nunca volvería a romper:"
    ],
    "cta": "Invita a los lectores a compartir sus propias reglas / contraejemplos / aportes.",
    "exemplar": {
      "headline": "Educativo / Técnico — Una publicación tipo framework",
      "body": "La mayoría de los equipos que diseñan sistemas multiagente se saltan la única pregunta que determina si el sistema funciona en producción: ¿quién es dueño de la decisión sobre qué sucede a continuación?\n\nEsta es la distinción entre orquestador y ejecutor, y la mayoría de los currículums la reducen a 'construí un sistema multiagente', lo cual no le dice nada a un responsable de contratación.\n\nAsí es como lo enseño:\n\n1. El ORQUESTADOR decide. Sostiene el objetivo, razona sobre qué hacer a continuación, enruta el trabajo. Es el plano de control.\n\n2. El EJECUTOR hace. Llama a la herramienta, ejecuta el paso del agente, lleva a cabo la decisión. Es el plano de datos.\n\nEstas son preocupaciones diferentes con requisitos de fiabilidad diferentes. Una falla del orquestador detiene el flujo de trabajo; una falla del ejecutor podría ser reintentable. Cuando mantienes estas preocupaciones separadas, puedes añadir tolerancia a fallos en la capa correcta.\n\nEjemplo concreto: un sistema de procesamiento de documentos con topología de supervisor en el que trabajé enrutaba 50K documentos por día entre 16 agentes especialistas. El supervisor (orquestador) hacía una clasificación y enrutamiento ligeros. Cada especialista (ejecutor) hacía un análisis profundo en su propio dominio.\n\nCuando un especialista fallaba, el supervisor enrutaba ese documento a un especialista de respaldo o a revisión humana. Cuando el propio supervisor tenía problemas, todo el pipeline se pausaba, que era el modo de falla correcto dados nuestros requisitos de cumplimiento.\n\nLo que esto NO resuelve: los problemas de coordinación emergente entre especialistas. Si dos ejecutores necesitan ponerse de acuerdo sobre un estado compartido, necesitas mensajería entre agentes como una preocupación aparte.\n\nLa regla: cuando describas un sistema multiagente, nombra explícitamente el orquestador y los ejecutores. Esa única distinción le indica a cualquier entrevistador que realmente has construido uno.\n\n¿Cuál es una distinción de sistemas multiagente que desearías que más gente entendiera?\n\n#AgenticAI #SoftwareArchitecture #LangGraph #AIAgents #MLOps",
      "whyItWorks": [
        "Abre señalando el error específico que comete la mayoría de los equipos: establece que el autor distingue la señal del ruido",
        "El framework numerado (1, 2) es escaneable: los lectores de LinkedIn hojean y luego se detienen en la estructura",
        "Ejemplo concreto con números de escala (50K docs/día, 16 agentes): convierte la 'publicación tipo framework' de teoría en práctica",
        "El explícito 'qué NO resuelve esto': genera credibilidad al no exagerar las afirmaciones, un movimiento que los creadores de contenido barato omiten"
      ]
    },
    "tier": "all_access"
  },
  {
    "slug": "engagement-prompt",
    "name": "Estímulo de Interacción",
    "purpose": "Impulsa los comentarios mediante una pregunta, una opinión provocadora o una encuesta. Señala interacción activa al algoritmo de LinkedIn y hace aflorar el pensamiento de tu audiencia.",
    "idealFrequency": "1–2 por semana",
    "structure": [
      "GANCHO — la pregunta polarizadora o no obvia (1–2 líneas)",
      "TU POSTURA FIRME — una opinión breve que invite al desacuerdo (2–3 líneas)",
      "EL FRAMEWORK QUE QUERRÍAS QUE USARAN LAS RESPUESTAS — muestra que has pensado la pregunta de forma estructural",
      "INVITACIÓN ABIERTA — pide a los lectores que opinen"
    ],
    "hookFormulas": [
      "Opinión impopular: [afirmación provocadora sobre IA/contratación/trabajo]",
      "Pregunta para quienes construyen agentic AI: [punto de decisión específico]",
      "Si tuvieras que elegir entre [A] y [B] en tu próximo sistema agéntico, ¿cuál elegirías?",
      "Todos dicen [sabiduría común]. Esta es la razón por la que creo que eso está equivocado:"
    ],
    "cta": "La publicación entera es el CTA: espera comentarios, no solo likes.",
    "exemplar": {
      "headline": "Estímulo de Interacción — Pregunta con opinión",
      "body": "Opinión impopular: la 'ingeniería de prompts' todavía no es una disciplina de ingeniería.\n\nSé que eso es una herejía en un mercado con puestos de 'Prompt Engineer' que pagan $250K. Pero esto es lo que realmente veo en los sistemas agénticos en producción:\n\nLa mayoría de la 'ingeniería de prompts' hoy es: escribir un prompt → probarlo → ver si funciona → ajustar el texto hasta que funcione.\n\nEso no es ingeniería. Es artesanía.\n\nLas disciplinas de ingeniería tienen:\n- Artefactos versionados con capacidad de rollback\n- Compuertas de pruebas (no solo impresiones vagas)\n- Medición cuantitativa de la calidad\n- Modos de falla documentados\n- Separación de preocupaciones\n\nLa ingeniería de prompts hoy no tiene nada de eso de forma sistemática. Los prompts viven en comentarios de código, cambian sin pruebas, se rompen sin monitoreo y se mantienen por conocimiento tribal.\n\nLos equipos que están ganando en agentic AI en producción son los que construyen estas piezas faltantes: versionado de prompts, conjuntos de pruebas golden, detección de drift, frameworks de A/B. No son 'ingenieros de prompts'. Son ingenieros que resulta que escriben prompts, y que construyeron la disciplina alrededor de ello.\n\n¿Es la ingeniería de prompts una disciplina de ingeniería real ya en tu organización, o sigue siendo artesanía disfrazada de ingeniería?\n\n#AgenticAI #PromptEngineering #MLOps #AIEngineering",
      "whyItWorks": [
        "Encabeza con 'Opinión impopular': los lectores de LinkedIn pasan de largo el consenso y se detienen en la controversia",
        "Respalda la opinión con criterios específicos (artefactos versionados, compuertas de pruebas, etc.), no solo con impresiones vagas",
        "No insulta a los ingenieros de prompts: distingue 'a las personas' de 'la madurez de la disciplina'",
        "Cierra con una pregunta que obliga a los lectores a tomar una posición en su propia respuesta: impulsores de comentarios, no de likes"
      ]
    },
    "tier": "all_access"
  }
],
};

// ---------------------------------------------------------------------------
// CONTENT CALENDAR + META PROMPTS
// ---------------------------------------------------------------------------

export const CONTENT_PROMPTS: Localized<LinkedInPrompt[]> = {
  en: [

  {
    slug: "weekly-content-calendar",
    section: "content",
    title: "Weekly Content Calendar",
    tagline: "Generate one week of LinkedIn posts aligned to the 4 archetypes.",
    whatItDoes: [
      "Produces a 7-day posting plan: 3 news commentary posts, 2 experience/insight posts, 2 educational posts, 1 engagement post (or similar mix).",
      "Each post has a specific topic, hook, and structure — enough for you to draft in 15 minutes using the archetype structure.",
      "Run every Sunday for the coming week. Pair with a news-gathering habit (following Anthropic, OpenAI, Google DeepMind, Meta AI blogs + TechCrunch + VentureBeat).",
    ],
    promptText: `You are a LinkedIn content strategist for agentic AI professionals. Generate a 7-day content calendar optimized for recruiter discovery and peer engagement.

[PASTE YOUR TARGET AUDIENCE — e.g., "hiring managers at top-tier AI labs" or "practitioners building production agentic systems"]
[PASTE YOUR 3 KEY EXPERTISE AREAS — e.g., "multi-agent orchestration, RAG at enterprise scale, production safety architecture"]
[LIST 3–5 AGENTIC AI NEWS STORIES OR PRODUCT RELEASES FROM THE LAST WEEK — or ask me for them and I'll list them]
[ANY SPECIFIC PROJECTS OR LESSONS YOU WANT TO SURFACE THIS WEEK]

## Produce a 7-day calendar with this mix:

Monday — News Commentary (pick one of the news stories)
Tuesday — Experience/Insight (draw from a real lesson in your expertise areas)
Wednesday — Educational/Technical (teach a framework or pattern)
Thursday — News Commentary (second news story)
Friday — Engagement Prompt (polarizing question or hot take)
Saturday — (skip or Experience/Insight)
Sunday — (skip)

For each post:
- Archetype + specific topic
- Suggested time window (post in 8–10 AM PT, 12–1 PM PT, or 5–7 PM PT for target audience)
- The HOOK (first 1–2 lines — LinkedIn truncates at ~200 chars without "see more")
- 3–5 bullet KEY POINTS the post should make
- Suggested CTA question for the close
- 3–5 recommended hashtags

## Also produce:
ENGAGEMENT STRATEGY FOR THIS WEEK
- 5 profiles you should thoughtfully comment on this week to signal engagement to the algorithm
- 2 conversations you should join in comments on major industry posts
- The cadence: when to post, when to comment, when to rest

QUALITY GATE
For each suggested post, confirm it passes the test: does this position me as someone who has SHIPPED agentic AI work, or as someone who reads about it? If the former, approve. If the latter, revise or swap.`,
    inputsRequired: [
      "Target audience description",
      "3 key expertise areas",
      "3–5 recent news stories (or ask the AI to surface them)",
      "Optional: projects or lessons to surface",
    ],
    timeEstimate: "20–30 minutes to generate; then 15 minutes per post to draft",
    tier: "all_access",
    agenticAIGuidance:
      "The best agentic AI content calendars are reactive, not prescriptive. Follow Anthropic, OpenAI, Google DeepMind, NVIDIA blogs; Hacker News top posts weekly; the Linux Foundation's Agentic AI Foundation announcements. Most weeks produce 2–3 high-value news events worth commenting on. The candidates who show up first on new frameworks (MCP, new LangGraph features, new safety tools) capture disproportionate follower growth during the reaction window.",
  },

  {
    slug: "hook-generator",
    section: "content",
    title: "Hook Generator",
    tagline: "Turn a draft post into a scroll-stopping opening.",
    whatItDoes: [
      "LinkedIn truncates posts at ~200 characters with a 'see more' link. If your first 200 characters don't earn the click, nothing else matters. Most drafts bury the interesting claim 3 paragraphs in.",
      "This prompt takes your draft and produces 5 alternative hooks that front-load the interesting claim — then lets you pick the best one.",
      "Run on every post before publishing. The 5-minute hook iteration is the single highest-ROI edit you can make.",
    ],
    promptText: `You are a LinkedIn copy editor specializing in agentic AI thought leadership. Generate 5 alternative opening hooks for the draft post below.

[PASTE YOUR DRAFT POST IN FULL]
[WHAT IS THE POST'S ARCHETYPE: News Commentary / Experience-Insight / Educational-Technical / Engagement]
[WHO IS THE TARGET AUDIENCE]

## Produce 5 alternative hooks, each in the 1–2 line range (max 200 characters for preview impact).

For each variant:
- The hook text itself
- Why this hook works for this specific post
- Which audience it optimizes for (hiring managers vs. peers vs. recruiters)
- Which archetype style it leans into ("Unpopular opinion", surprising stat, question, etc.)

Then recommend THE STRONGEST HOOK with a one-sentence rationale.

## Test the hook:
For the recommended hook, ask these questions:
- Does the reader WANT to click "see more" after reading just this hook?
- Would a hiring manager at a top-tier AI lab find this worth a 3-second investment?
- Is the hook specific enough that it wouldn't work for a different topic?

If any answer is no, produce one more alternative hook that fixes the gap.`,
    inputsRequired: [
      "Your draft post (full text)",
      "Post archetype",
      "Target audience",
    ],
    timeEstimate: "5 minutes per post",
    tier: "all_access",
    agenticAIGuidance:
      "For agentic AI content, the most effective hooks in 2026 share three properties: (1) they cite a specific number (install count, accuracy improvement, production outage duration) — not vague claims; (2) they pivot from surface to deep pattern ('the real story is…'); (3) they acknowledge a mistake or humility moment — LinkedIn rewards vulnerability in technical posts because most content creators are falsely confident. Hooks that fail usually over-claim or stay too generic ('AI is changing everything').",
  },
  ],
  es: [
  {
    "slug": "weekly-content-calendar",
    "section": "content",
    "title": "Calendario de Contenido Semanal",
    "tagline": "Genera una semana de publicaciones de LinkedIn alineadas con los 4 arquetipos.",
    "whatItDoes": [
      "Produce un plan de publicación de 7 días: 3 publicaciones de comentario sobre noticias, 2 publicaciones de experiencia/reflexión, 2 publicaciones educativas, 1 publicación de interacción (o una combinación similar).",
      "Cada publicación tiene un tema específico, un gancho y una estructura — suficiente para que redactes en 15 minutos usando la estructura del arquetipo.",
      "Ejecútalo cada domingo para la semana entrante. Combínalo con un hábito de recopilación de noticias (siguiendo los blogs de Anthropic, OpenAI, Google DeepMind, Meta AI + TechCrunch + VentureBeat)."
    ],
    "promptText": "Eres un estratega de contenido de LinkedIn para profesionales de agentic AI. Genera un calendario de contenido de 7 días optimizado para el descubrimiento por parte de reclutadores y la interacción con colegas.\n\n[PEGA TU AUDIENCIA OBJETIVO — p. ej., \"responsables de contratación en laboratorios de AI de primer nivel\" o \"profesionales que construyen sistemas agénticos en producción\"]\n[PEGA TUS 3 ÁREAS CLAVE DE EXPERIENCIA — p. ej., \"orquestación multiagente, RAG a escala empresarial, arquitectura de seguridad en producción\"]\n[ENUMERA 3–5 NOTICIAS O LANZAMIENTOS DE PRODUCTOS DE AGENTIC AI DE LA ÚLTIMA SEMANA — o pídemelos y los enumeraré]\n[CUALQUIER PROYECTO O LECCIÓN ESPECÍFICA QUE QUIERAS DESTACAR ESTA SEMANA]\n\n## Produce un calendario de 7 días con esta combinación:\n\nLunes — Comentario sobre noticias (elige una de las noticias)\nMartes — Experiencia/Reflexión (basada en una lección real de tus áreas de experiencia)\nMiércoles — Educativo/Técnico (enseña un marco o patrón)\nJueves — Comentario sobre noticias (segunda noticia)\nViernes — Estímulo de interacción (pregunta polarizante u opinión provocadora)\nSábado — (omitir o Experiencia/Reflexión)\nDomingo — (omitir)\n\nPara cada publicación:\n- Arquetipo + tema específico\n- Franja horaria sugerida (publica de 8–10 AM PT, 12–1 PM PT, o 5–7 PM PT para la audiencia objetivo)\n- El GANCHO (las primeras 1–2 líneas — LinkedIn trunca en ~200 caracteres sin el \"ver más\")\n- 3–5 PUNTOS CLAVE en viñetas que la publicación debe transmitir\n- Pregunta de CTA sugerida para el cierre\n- 3–5 hashtags recomendados\n\n## Produce también:\nESTRATEGIA DE INTERACCIÓN PARA ESTA SEMANA\n- 5 perfiles en los que deberías comentar de forma reflexiva esta semana para señalar tu interacción al algoritmo\n- 2 conversaciones a las que deberías unirte en los comentarios de publicaciones importantes del sector\n- La cadencia: cuándo publicar, cuándo comentar, cuándo descansar\n\nCONTROL DE CALIDAD\nPara cada publicación sugerida, confirma que pasa la prueba: ¿esto me posiciona como alguien que ha LANZADO trabajo de agentic AI, o como alguien que lee sobre ello? Si es lo primero, apruébala. Si es lo segundo, revísala o cámbiala.",
    "inputsRequired": [
      "Descripción de la audiencia objetivo",
      "3 áreas clave de experiencia",
      "3–5 noticias recientes (o pídele a la AI que las identifique)",
      "Opcional: proyectos o lecciones a destacar"
    ],
    "timeEstimate": "20–30 minutos para generar; luego 15 minutos por publicación para redactar",
    "tier": "all_access",
    "agenticAIGuidance": "Los mejores calendarios de contenido de agentic AI son reactivos, no prescriptivos. Sigue los blogs de Anthropic, OpenAI, Google DeepMind, NVIDIA; las publicaciones destacadas semanales de Hacker News; los anuncios de la Agentic AI Foundation de la Linux Foundation. La mayoría de las semanas producen 2–3 eventos noticiosos de alto valor que vale la pena comentar. Los candidatos que aparecen primero sobre nuevos marcos (MCP, nuevas funciones de LangGraph, nuevas herramientas de seguridad) capturan un crecimiento desproporcionado de seguidores durante la ventana de reacción."
  },
  {
    "slug": "hook-generator",
    "section": "content",
    "title": "Generador de Ganchos",
    "tagline": "Convierte un borrador de publicación en una apertura que detiene el scroll.",
    "whatItDoes": [
      "LinkedIn trunca las publicaciones en ~200 caracteres con un enlace de 'ver más'. Si tus primeros 200 caracteres no ganan el clic, nada más importa. La mayoría de los borradores entierran la afirmación interesante 3 párrafos más adentro.",
      "Este prompt toma tu borrador y produce 5 ganchos alternativos que colocan al frente la afirmación interesante — y luego te deja elegir el mejor.",
      "Ejecútalo en cada publicación antes de publicar. La iteración de gancho de 5 minutos es la edición de mayor ROI que puedes hacer."
    ],
    "promptText": "Eres un editor de textos de LinkedIn especializado en liderazgo de opinión sobre agentic AI. Genera 5 ganchos de apertura alternativos para el borrador de publicación a continuación.\n\n[PEGA TU BORRADOR DE PUBLICACIÓN COMPLETO]\n[CUÁL ES EL ARQUETIPO DE LA PUBLICACIÓN: Comentario sobre noticias / Experiencia-Reflexión / Educativo-Técnico / Interacción]\n[QUIÉN ES LA AUDIENCIA OBJETIVO]\n\n## Produce 5 ganchos alternativos, cada uno en el rango de 1–2 líneas (máximo 200 caracteres para el impacto en la vista previa).\n\nPara cada variante:\n- El texto del gancho en sí\n- Por qué este gancho funciona para esta publicación específica\n- Para qué audiencia optimiza (responsables de contratación vs. colegas vs. reclutadores)\n- En qué estilo de arquetipo se apoya (\"Opinión impopular\", estadística sorprendente, pregunta, etc.)\n\nLuego recomienda EL GANCHO MÁS FUERTE con una justificación de una oración.\n\n## Prueba el gancho:\nPara el gancho recomendado, hazte estas preguntas:\n- ¿El lector QUIERE hacer clic en \"ver más\" después de leer solo este gancho?\n- ¿Un responsable de contratación en un laboratorio de AI de primer nivel consideraría que vale una inversión de 3 segundos?\n- ¿Es el gancho lo suficientemente específico como para que no funcionaría para un tema diferente?\n\nSi alguna respuesta es no, produce un gancho alternativo más que cierre la brecha.",
    "inputsRequired": [
      "Tu borrador de publicación (texto completo)",
      "Arquetipo de la publicación",
      "Audiencia objetivo"
    ],
    "timeEstimate": "5 minutos por publicación",
    "tier": "all_access",
    "agenticAIGuidance": "Para el contenido de agentic AI, los ganchos más efectivos en 2026 comparten tres propiedades: (1) citan un número específico (número de instalaciones, mejora de precisión, duración de una caída en producción) — no afirmaciones vagas; (2) pivotan de lo superficial al patrón profundo ('la verdadera historia es…'); (3) reconocen un error o un momento de humildad — LinkedIn premia la vulnerabilidad en las publicaciones técnicas porque la mayoría de los creadores de contenido tienen una confianza falsa. Los ganchos que fallan suelen exagerar la afirmación o quedarse demasiado genéricos ('la AI lo está cambiando todo')."
  }
],
};

// ---------------------------------------------------------------------------
// OUTREACH TEMPLATES
// ---------------------------------------------------------------------------

export const OUTREACH_TEMPLATES: Localized<OutreachTemplate[]> = {
  en: [

  {
    slug: "practitioner-connection",
    persona: "Practitioner at a target company",
    goal: "Build genuine relationship before any ask. Get onto their radar for future referrals without positioning as a desperate job seeker.",
    principle: "Lead with curiosity, not job-seeking. Reference their specific work.",
    template: `Hi [Name],

I noticed your work on [specific project, talk, or post — be specific, not "your AI work"]. I've been working in [adjacent space] and recently built [your relevant project] — your [specific technique / framework choice / architectural decision] was one of the things that helped me think through [specific problem you hit].

Would you be open to a 15-minute call? I'd love to learn how you thought about [specific open question you have about their work].

Either way, thanks for the work you're sharing publicly.

[Your name]`,
    doExample: `Hi Priya,

I noticed your talk at LangChain Live on 'Production HITL for Finance Agents' — your point about tail-risk cases requiring human escalation rewrote how I was thinking about my current project. I just shipped a multi-agent pipeline at a healthtech Series C, and the HITL design was the hardest part.

Would you be open to a 15-min call? I'd love to learn how you calibrated the confidence threshold for human escalation — that's the one I'm still tuning.

Either way, thanks for the talk. Sent it to two colleagues.

Alex`,
    dontExample: `Hi Priya,

I saw your profile and we seem to be working in similar areas. I'm transitioning into agentic AI roles and would really appreciate 15 minutes of your time to discuss your career path and any advice you might have. I'm very motivated and would love to learn from someone with your experience.

Thanks in advance,
Alex`,
    followUp: `Hi [Name],

Circling back — I know calendars are full this time of year. If next week is easier, here's my Calendly: [link]. If not, no pressure at all. I'll ping you if I put a blog post together on [the topic you discussed]; your comment on [specific point] would improve it.

[Your name]`,
  },

  {
    slug: "hiring-manager-outreach",
    persona: "Hiring manager at a target company (after applying)",
    goal: "Convert a cold application into a warm introduction. Bypass the ATS black hole.",
    principle: "Reference the specific job, your specific fit, and one specific thing you already know about their work — under 150 words.",
    template: `Hi [Name],

I applied for the [exact role title] role on [date]. Given the [specific requirement from the JD that matches your background], I thought a direct line might help.

I've spent [X] years building [specific relevant work]. The piece of the JD that jumped out was [specific bullet] — at my last role, I [specific matching accomplishment with a number].

I know your team is working on [specific product / team focus you researched]. I'd love to be considered, and I'd welcome 15 minutes to discuss fit if that's useful.

Application link: [paste]

[Your name]`,
    doExample: `Hi Sarah,

I applied for the Senior Agentic AI Engineer role on Tuesday. Given the JD's emphasis on production multi-agent systems at SLA, I thought a direct line might help.

I've spent 4 years building production agentic systems at a healthtech Series C. The piece of the JD that jumped out was 'owns distributed tracing across agent hops' — at my last role, I instrumented OpenTelemetry across 8 specialist agents processing 50K documents/day, which cut our p99 debug time from 4 hours to under 20 minutes.

I know your team is working on the document processing pipeline for [specific enterprise segment] — happy to be considered, and welcome 15 minutes to discuss fit if that's useful.

Application ID: #ENG-2026-0483

Alex`,
    dontExample: `Hi Sarah,

I just applied for a role at your company and wanted to follow up. I think I would be a great fit and have extensive experience in AI and machine learning. I've attached my resume and would love to schedule a call to discuss opportunities at your company.

Looking forward to hearing from you,
Alex`,
  },

  {
    slug: "peer-transitioner-connection",
    persona: "Peer career transitioner in the same target space",
    goal: "Build a peer support network. Reciprocal value — you're not asking for favors, you're building a community.",
    principle: "Name the shared transition explicitly. Offer value (resource, reflection, intro) as part of the first message.",
    template: `Hi [Name],

Noticed we're both pivoting into [target space — e.g., "agentic AI engineering from traditional ML"] — your post on [their recent reflection/project] resonated.

Sharing two things:
1. [A resource that helped you — paper, blog, course]
2. [A reflection or open question you'd love their take on]

Happy to trade notes on the transition whenever. No agenda.

[Your name]`,
    doExample: `Hi Marcus,

Noticed we're both pivoting from traditional MLE into agentic AI — your post on 'going from scikit-learn to LangGraph' nailed the shift I'm still working through.

Sharing two things:
1. Anthropic's 'Building Effective Agents' guide rewrote how I think about when to use a single-agent vs. multi-agent pattern
2. Genuine question: how are you approaching interview prep for the 'design a multi-agent system' question? I'm building a personal project for this and deciding whether a supervisor or peer-to-peer topology is the more defensible choice to walk through.

Happy to trade notes on the transition. No agenda.

Alex`,
    dontExample: `Hi Marcus,

I'm also transitioning into AI and would love to connect with others doing the same. Let me know if you'd like to network!

Alex`,
  },

  {
    slug: "recruiter-direct",
    persona: "AI/ML recruiter at a target company",
    goal: "Put yourself on their sourcing radar. Unlike cold applications, recruiters actively surface candidates to hiring managers.",
    principle: "Show you know what they source for. Make it easy for them to reach back.",
    template: `Hi [Name],

I understand you source for [specific roles] at [Company]. I'm actively exploring [specific role level — e.g., 'Senior Agentic AI Engineer positions'] and wanted to be on your radar.

One-pager fit summary:
- [Your target role] with [X] years in [specific domain]
- Shipped [specific production agentic system] at [scale signal]
- Previously [context-setting role or credential]
- Open to [location preference + remote/hybrid]

Happy to send a tailored resume and have a 15-minute call if any of your current searches match. My LinkedIn profile has the detail.

[Your name]`,
    doExample: `Hi Jen,

I understand you source for AI engineering roles at Anthropic. I'm actively exploring Senior Agentic AI Engineer positions and wanted to be on your radar.

One-pager fit summary:
- 4 years in production agentic systems, LangGraph + MCP
- Shipped a multi-agent document processing pipeline (50K docs/day, 99.95% SLA) at a Series C healthtech
- Previously MLE at a top-10 US tech company
- Open to Bay Area or remote

Happy to send a tailored resume and 15 minutes if any current searches match. My LinkedIn profile has the detail.

Alex Chen`,
    dontExample: `Hi Jen,

I'm looking for AI jobs and saw you work at Anthropic. Do you have any openings? I'd love to be considered for any role that fits my background.

Thanks,
Alex`,
  },
  ],
  es: [
  {
    "slug": "practitioner-connection",
    "persona": "Profesional en una empresa objetivo",
    "goal": "Construir una relación genuina antes de cualquier petición. Ponerte en su radar para futuras recomendaciones sin posicionarte como un candidato desesperado.",
    "principle": "Guíate por la curiosidad, no por la búsqueda de empleo. Haz referencia a su trabajo específico.",
    "template": "Hola [Name]:\n\nNoté tu trabajo en [proyecto, charla o publicación específica — sé específico, no \"tu trabajo en IA\"]. He estado trabajando en [espacio adyacente] y recientemente construí [tu proyecto relevante] — tu [técnica específica / elección de framework / decisión arquitectónica] fue una de las cosas que me ayudó a pensar en [problema específico con el que te topaste].\n\n¿Estarías abierto a una llamada de 15 minutos? Me encantaría conocer cómo pensaste sobre [pregunta abierta específica que tienes sobre su trabajo].\n\nDe cualquier manera, gracias por el trabajo que compartes públicamente.\n\n[Your name]",
    "doExample": "Hola Priya:\n\nNoté tu charla en LangChain Live sobre 'Production HITL for Finance Agents' — tu punto sobre los casos de riesgo de cola que requieren escalamiento humano reescribió mi forma de pensar sobre mi proyecto actual. Acabo de lanzar un pipeline multiagente en una healthtech Serie C, y el diseño de HITL fue la parte más difícil.\n\n¿Estarías abierta a una llamada de 15 minutos? Me encantaría conocer cómo calibraste el umbral de confianza para el escalamiento humano — ese es el que sigo ajustando.\n\nDe cualquier manera, gracias por la charla. La envié a dos colegas.\n\nAlex",
    "dontExample": "Hola Priya:\n\nVi tu perfil y parece que trabajamos en áreas similares. Estoy en transición hacia roles de IA agéntica y realmente agradecería 15 minutos de tu tiempo para hablar sobre tu trayectoria profesional y cualquier consejo que puedas tener. Estoy muy motivado y me encantaría aprender de alguien con tu experiencia.\n\nGracias de antemano,\nAlex",
    "followUp": "Hola [Name]:\n\nRetomando el hilo — sé que las agendas están llenas en esta época del año. Si la próxima semana te resulta más fácil, aquí está mi Calendly: [link]. Si no, sin ninguna presión. Te escribiré si preparo una publicación de blog sobre [el tema que discutimos]; tu comentario sobre [punto específico] la mejoraría.\n\n[Your name]"
  },
  {
    "slug": "hiring-manager-outreach",
    "persona": "Responsable de contratación en una empresa objetivo (después de postular)",
    "goal": "Convertir una postulación en frío en una presentación cálida. Evita el agujero negro del ATS.",
    "principle": "Haz referencia al puesto específico, a tu encaje específico y a algo específico que ya sabes sobre su trabajo — en menos de 150 palabras.",
    "template": "Hola [Name]:\n\nPostulé para el puesto de [título exacto del rol] el [fecha]. Dado el [requisito específico de la descripción del puesto que coincide con tu experiencia], pensé que una línea directa podría ayudar.\n\nHe pasado [X] años construyendo [trabajo relevante específico]. La parte de la descripción del puesto que me llamó la atención fue [punto específico] — en mi último rol, [logro específico coincidente con un número].\n\nSé que tu equipo está trabajando en [producto específico / enfoque del equipo que investigaste]. Me encantaría ser considerado, y agradecería 15 minutos para hablar sobre el encaje si te resulta útil.\n\nEnlace de la postulación: [pegar]\n\n[Your name]",
    "doExample": "Hola Sarah:\n\nPostulé para el puesto de Senior Agentic AI Engineer el martes. Dado el énfasis de la descripción del puesto en sistemas multiagente en producción con SLA, pensé que una línea directa podría ayudar.\n\nHe pasado 4 años construyendo sistemas agénticos en producción en una healthtech Serie C. La parte de la descripción del puesto que me llamó la atención fue 'owns distributed tracing across agent hops' — en mi último rol, instrumenté OpenTelemetry en 8 agentes especialistas que procesaban 50K documentos/día, lo que redujo nuestro tiempo de depuración p99 de 4 horas a menos de 20 minutos.\n\nSé que tu equipo está trabajando en el pipeline de procesamiento de documentos para [segmento empresarial específico] — encantado de ser considerado, y agradecería 15 minutos para hablar sobre el encaje si te resulta útil.\n\nID de postulación: #ENG-2026-0483\n\nAlex",
    "dontExample": "Hola Sarah:\n\nAcabo de postular para un puesto en tu empresa y quería dar seguimiento. Creo que sería una excelente opción y tengo amplia experiencia en IA y machine learning. Adjunté mi currículum y me encantaría agendar una llamada para hablar sobre oportunidades en tu empresa.\n\nQuedo a la espera de tu respuesta,\nAlex"
  },
  {
    "slug": "peer-transitioner-connection",
    "persona": "Colega en transición profesional hacia el mismo espacio objetivo",
    "goal": "Construir una red de apoyo entre pares. Valor recíproco — no pides favores, construyes una comunidad.",
    "principle": "Nombra la transición compartida de forma explícita. Ofrece valor (recurso, reflexión, presentación) como parte del primer mensaje.",
    "template": "Hola [Name]:\n\nNoté que ambos estamos pivotando hacia [espacio objetivo — p. ej., \"ingeniería de IA agéntica desde ML tradicional\"] — tu publicación sobre [su reflexión/proyecto reciente] resonó conmigo.\n\nComparto dos cosas:\n1. [Un recurso que te ayudó — paper, blog, curso]\n2. [Una reflexión o pregunta abierta sobre la que te encantaría su opinión]\n\nEncantado de intercambiar notas sobre la transición cuando sea. Sin agenda.\n\n[Your name]",
    "doExample": "Hola Marcus:\n\nNoté que ambos estamos pivotando desde MLE tradicional hacia IA agéntica — tu publicación sobre 'going from scikit-learn to LangGraph' capturó exactamente el cambio en el que sigo trabajando.\n\nComparto dos cosas:\n1. La guía 'Building Effective Agents' de Anthropic reescribió mi forma de pensar sobre cuándo usar un patrón de agente único vs. multiagente\n2. Pregunta genuina: ¿cómo estás abordando la preparación de entrevistas para la pregunta de 'diseña un sistema multiagente'? Estoy construyendo un proyecto personal para esto y decidiendo si una topología supervisor o peer-to-peer es la opción más defendible para explicar.\n\nEncantado de intercambiar notas sobre la transición. Sin agenda.\n\nAlex",
    "dontExample": "Hola Marcus:\n\nYo también estoy en transición hacia la IA y me encantaría conectar con otros que estén haciendo lo mismo. ¡Avísame si te gustaría hacer networking!\n\nAlex"
  },
  {
    "slug": "recruiter-direct",
    "persona": "Reclutador de IA/ML en una empresa objetivo",
    "goal": "Ponerte en su radar de sourcing. A diferencia de las postulaciones en frío, los reclutadores presentan candidatos activamente a los responsables de contratación.",
    "principle": "Muestra que sabes qué perfiles buscan. Facilítales el contacto de vuelta.",
    "template": "Hola [Name]:\n\nEntiendo que reclutas para [roles específicos] en [Company]. Estoy explorando activamente [nivel específico del rol — p. ej., 'posiciones de Senior Agentic AI Engineer'] y quería estar en tu radar.\n\nResumen de encaje de una página:\n- [Tu rol objetivo] con [X] años en [dominio específico]\n- Lancé [sistema agéntico en producción específico] a [señal de escala]\n- Anteriormente [rol o credencial que da contexto]\n- Abierto a [preferencia de ubicación + remoto/híbrido]\n\nEncantado de enviar un currículum personalizado y tener una llamada de 15 minutos si alguna de tus búsquedas actuales coincide. Mi perfil de LinkedIn tiene el detalle.\n\n[Your name]",
    "doExample": "Hola Jen:\n\nEntiendo que reclutas para roles de ingeniería de IA en Anthropic. Estoy explorando activamente posiciones de Senior Agentic AI Engineer y quería estar en tu radar.\n\nResumen de encaje de una página:\n- 4 años en sistemas agénticos en producción, LangGraph + MCP\n- Lancé un pipeline multiagente de procesamiento de documentos (50K docs/día, SLA del 99.95%) en una healthtech Serie C\n- Anteriormente MLE en una empresa tecnológica del top-10 de EE. UU.\n- Abierto a Bay Area o remoto\n\nEncantado de enviar un currículum personalizado y 15 minutos si alguna búsqueda actual coincide. Mi perfil de LinkedIn tiene el detalle.\n\nAlex Chen",
    "dontExample": "Hola Jen:\n\nEstoy buscando trabajos de IA y vi que trabajas en Anthropic. ¿Tienes alguna vacante? Me encantaría ser considerado para cualquier rol que encaje con mi perfil.\n\nGracias,\nAlex"
  }
],
};

// ---------------------------------------------------------------------------
// ANALYTICS
// ---------------------------------------------------------------------------

export const ANALYTICS_PROMPTS: Localized<LinkedInPrompt[]> = {
  en: [

  {
    slug: "monthly-linkedin-health-check",
    section: "analytics",
    title: "Monthly LinkedIn Health Check",
    tagline: "Measure what's working. Cut what isn't.",
    whatItDoes: [
      "Runs a monthly analytics review against three signals: profile visibility (search appearances, profile views), content performance (post impressions + engagement rate), and conversion (recruiter InMails, connection quality).",
      "Identifies which posts outperformed (replicate the pattern) and which underperformed (cut or revise).",
      "Run the first week of each month. Pair with the monthly Resume Mastery progress review for a full job-search cadence.",
    ],
    promptText: `You are a LinkedIn analytics advisor. Run a monthly health check on my LinkedIn presence.

## Pull these metrics from LinkedIn Analytics for the last 30 days:
[SEARCH APPEARANCES]: (how many times your profile appeared in recruiter searches)
[PROFILE VIEWS]: (total unique viewers)
[POST IMPRESSIONS]: (total across all posts)
[ENGAGEMENT RATE]: (reactions + comments + shares / impressions)
[TOP 3 POSTS THIS MONTH]: (name + impressions + engagement)
[BOTTOM 3 POSTS THIS MONTH]: (name + impressions)
[CONNECTION ACCEPTS]: (number of new connections)
[RECRUITER INMAILS RECEIVED]: (count + which companies)
[KEYWORDS APPEARING IN SEARCHES]: (from Dashboard analytics)

## Produce:

MONTH-OVER-MONTH TRAJECTORY
| Metric | Last Month | This Month | Change % |
For each: Is this on pace for my goals? (assume goal is 5x profile views, 5–15 recruiter InMails/month)

WHAT WORKED
The top 3 posts this month — identify the pattern. Was it the hook type? Topic? Archetype? Timing? Look for the reproducible signal.

WHAT DIDN'T
The bottom 3 posts — why did they underperform? Was it weak hook, off-audience topic, bad timing, wrong archetype? For each, recommend: repost with fix OR cut this topic from the calendar.

SEARCH KEYWORD ANALYSIS
Which keywords are appearing in the searches that found me? Are they the target-role keywords, or am I ranking for tangential terms?

NEXT MONTH ACTIONS
Three specific changes for next month based on the data:
1. Double down on [pattern that worked]
2. Cut or revise [pattern that didn't]
3. Fill [gap identified in keyword or audience reach]

SUSTAINABILITY CHECK
Am I posting 8–10 times per week as planned? If volume has dropped, what's the blocker?
Am I engaging (comments on others' posts) 10+ times per week? If not, what needs to change?`,
    inputsRequired: [
      "30-day snapshot from LinkedIn Analytics (search appearances, profile views, post impressions, engagement rate)",
      "Top 3 and bottom 3 posts from the month",
      "Recruiter InMail count",
    ],
    timeEstimate: "20 minutes monthly",
    tier: "all_access",
    agenticAIGuidance:
      "For agentic AI candidates specifically, the search appearance and recruiter InMail metrics matter more than raw follower count or impressions. A 500-follower profile that gets 12 recruiter InMails per month is outperforming a 5,000-follower profile with zero InMails — the second is ranking for 'AI thought leader' not 'hireable agentic AI engineer'. Optimize for the former metric.",
  },
  ],
  es: [
  {
    "slug": "monthly-linkedin-health-check",
    "section": "analytics",
    "title": "Chequeo Mensual de Salud de LinkedIn",
    "tagline": "Mide lo que funciona. Elimina lo que no.",
    "whatItDoes": [
      "Realiza una revisión mensual de analíticas frente a tres señales: visibilidad del perfil (apariciones en búsquedas, vistas del perfil), rendimiento del contenido (impresiones de publicaciones + tasa de interacción) y conversión (InMails de reclutadores, calidad de las conexiones).",
      "Identifica qué publicaciones tuvieron un rendimiento superior (replica el patrón) y cuáles quedaron por debajo (elimínalas o revísalas).",
      "Ejecútalo la primera semana de cada mes. Combínalo con la revisión mensual de progreso de Resume Mastery para tener una cadencia completa de búsqueda de empleo."
    ],
    "promptText": "Eres un asesor de analíticas de LinkedIn. Realiza un chequeo mensual de salud sobre mi presencia en LinkedIn.\n\n## Extrae estas métricas de LinkedIn Analytics de los últimos 30 días:\n[SEARCH APPEARANCES]: (cuántas veces apareció tu perfil en búsquedas de reclutadores)\n[PROFILE VIEWS]: (total de visitantes únicos)\n[POST IMPRESSIONS]: (total en todas las publicaciones)\n[ENGAGEMENT RATE]: (reacciones + comentarios + veces compartido / impresiones)\n[TOP 3 POSTS THIS MONTH]: (nombre + impresiones + interacción)\n[BOTTOM 3 POSTS THIS MONTH]: (nombre + impresiones)\n[CONNECTION ACCEPTS]: (número de nuevas conexiones)\n[RECRUITER INMAILS RECEIVED]: (cantidad + qué empresas)\n[KEYWORDS APPEARING IN SEARCHES]: (de las analíticas del Dashboard)\n\n## Produce:\n\nTRAYECTORIA MES A MES\n| Métrica | Mes Pasado | Este Mes | Cambio % |\nPara cada una: ¿Va al ritmo de mis objetivos? (asume que el objetivo es multiplicar por 5 las vistas del perfil, 5–15 InMails de reclutadores al mes)\n\nLO QUE FUNCIONÓ\nLas 3 mejores publicaciones de este mes — identifica el patrón. ¿Fue el tipo de gancho? ¿El tema? ¿El arquetipo? ¿El momento? Busca la señal reproducible.\n\nLO QUE NO FUNCIONÓ\nLas 3 peores publicaciones — ¿por qué tuvieron un rendimiento inferior? ¿Fue un gancho débil, un tema fuera de audiencia, mal momento, arquetipo equivocado? Para cada una, recomienda: volver a publicar con un ajuste O eliminar este tema del calendario.\n\nANÁLISIS DE PALABRAS CLAVE DE BÚSQUEDA\n¿Qué palabras clave aparecen en las búsquedas que me encontraron? ¿Son las palabras clave del rol objetivo, o estoy posicionándome por términos tangenciales?\n\nACCIONES PARA EL PRÓXIMO MES\nTres cambios específicos para el próximo mes basados en los datos:\n1. Redoblar la apuesta en [patrón que funcionó]\n2. Eliminar o revisar [patrón que no funcionó]\n3. Cubrir [brecha identificada en palabras clave o alcance de audiencia]\n\nVERIFICACIÓN DE SOSTENIBILIDAD\n¿Estoy publicando 8–10 veces por semana como estaba planeado? Si el volumen bajó, ¿cuál es el obstáculo?\n¿Estoy interactuando (comentarios en publicaciones de otros) más de 10 veces por semana? Si no, ¿qué necesita cambiar?",
    "inputsRequired": [
      "Instantánea de 30 días de LinkedIn Analytics (apariciones en búsquedas, vistas del perfil, impresiones de publicaciones, tasa de interacción)",
      "Las 3 mejores y las 3 peores publicaciones del mes",
      "Cantidad de InMails de reclutadores"
    ],
    "timeEstimate": "20 minutos al mes",
    "tier": "all_access",
    "agenticAIGuidance": "Para los candidatos de agentic AI en particular, las métricas de apariciones en búsquedas e InMails de reclutadores importan más que el número bruto de seguidores o impresiones. Un perfil de 500 seguidores que recibe 12 InMails de reclutadores al mes está superando a un perfil de 5,000 seguidores con cero InMails — el segundo se está posicionando como 'líder de opinión en IA' y no como 'ingeniero de agentic AI contratable'. Optimiza para la primera métrica."
  }
],
};

// ---------------------------------------------------------------------------
// Registry + lookups
// ---------------------------------------------------------------------------

// ALL_PROMPTS concatenates the already-resolved `.en`/`.es` arrays of the
// other Localized constants above -- keeps this a plain Localized<T[]>
// constant (consistent with the rest of the file) rather than a
// locale-parameterized function.
export const ALL_PROMPTS: Localized<LinkedInPrompt[]> = {
  en: [
    ...PROFILE_PROMPTS.en,
    ...CONTENT_PROMPTS.en,
    ...ANALYTICS_PROMPTS.en,
  ],
  es: [
    ...(PROFILE_PROMPTS.es ?? PROFILE_PROMPTS.en),
    ...(CONTENT_PROMPTS.es ?? CONTENT_PROMPTS.en),
    ...(ANALYTICS_PROMPTS.es ?? ANALYTICS_PROMPTS.en),
  ],
};

export function getPromptBySlug(
  slug: string,
  locale: string = "en",
): LinkedInPrompt | undefined {
  return L(ALL_PROMPTS, locale).find((p) => p.slug === slug);
}

export function getPromptsForSection(
  section: "profile" | "content" | "outreach" | "analytics",
  locale: string = "en",
): LinkedInPrompt[] {
  return L(ALL_PROMPTS, locale).filter((p) => p.section === section);
}

export function getArchetypeBySlug(
  slug: string,
  locale: string = "en",
): PostArchetype | undefined {
  return L(POST_ARCHETYPES, locale).find((a) => a.slug === slug);
}

export function getOutreachTemplateBySlug(
  slug: string,
  locale: string = "en",
): OutreachTemplate | undefined {
  return L(OUTREACH_TEMPLATES, locale).find((t) => t.slug === slug);
}
